<template>
  <div id="user-edit-tab-info">
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="vx-col w-full mb-base">
          <vs-progress
            v-if="isLoadingRecords"
            indeterminate
            color="primary"
            class="mb-4"
          />
          <div v-if="loadError" class="hours-rendered-tab-error mb-4">
            <span>Earnings records could not be loaded.</span>
            <vs-button type="border" size="small" @click="loadEarningsData">
              Try again
            </vs-button>
          </div>
          <div
            v-if="!isLoadingRecords && !loadError"
            class="hours-rendered-record-switch mb-4"
          >
            <button
              type="button"
              :class="{ active: recordView === 'active' }"
              @click="recordView = 'active'"
            >
              <feather-icon icon="CheckCircleIcon" svgClasses="h-4 w-4" />
              Current Active
              <span>{{ activeEarnings.length }}</span>
            </button>
            <button
              type="button"
              :class="{ active: recordView === 'history' }"
              @click="recordView = 'history'"
            >
              <feather-icon icon="ClockIcon" svgClasses="h-4 w-4" />
              History
              <span>{{ earningsHistory.length }}</span>
            </button>
          </div>
          <template>
            <vs-table
              v-if="!isLoadingRecords && sortedData.length"
              stripe
              max-items="4"
              pagination
              search
              :data="sortedData"
            >
              <template slot="thead">
                <vs-th @click.native="sort('type')">Type</vs-th>
                <vs-th @click.native="sort('amount')">Amount</vs-th>
                <vs-th @click.native="sort('period')">Period</vs-th>
                <vs-th @click.native="sort('oneTime')">OneTime?</vs-th>
                <vs-th @click.native="sort('hold')">Hold?</vs-th>
                <vs-th @click.native="sort('active')">Active?</vs-th>
                <vs-th @click.native="sort('includeIn13thMonth')"
                  >Include In 13th Month?</vs-th
                >
                <vs-th @click.native="sort('includeInFinalPay')"
                  >Include In Final Pay?</vs-th
                >
                <vs-th @click.native="sort('effectivityDate')"
                  >Effectivity Date</vs-th
                >
                <vs-th @click.native="sort('expiryDate')">Expiry Date</vs-th>
                <vs-th @click.native="sort('resumeDate')">Resume Date</vs-th>
              </template>
              <template slot-scope="{ data }">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="data[indextr].earningsTypeId">
                    {{ earningsTypeLabel(data[indextr].earningsTypeId) }}
                  </vs-td>
                  <vs-td :data="data[indextr].earningsAmount">
                    {{ data[indextr].earningsAmount }}
                  </vs-td>

                  <vs-td :data="data[indextr].periodId">
                    {{ periodLabel(data[indextr].periodId) }}
                  </vs-td>
                  <vs-td :data="data[indextr].isOneTime">
                    <vs-checkbox
                      v-model="data[indextr].isOneTime"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td :data="data[indextr].isHold">
                    <vs-checkbox
                      v-model="data[indextr].isHold"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td :data="data[indextr].isActive">
                    <vs-checkbox
                      v-model="data[indextr].isActive"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td :data="data[indextr].isIncludedIn13thMonth">
                    <vs-checkbox
                      v-model="data[indextr].isIncludedIn13thMonth"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td :data="data[indextr].isIncludedInFinalPay">
                    <vs-checkbox
                      v-model="data[indextr].isIncludedInFinalPay"
                      class="pointer-events-none"
                    />
                  </vs-td>
                  <vs-td :data="data[indextr].effectivityDate">
                    {{ data[indextr].effectivityDate | fulldate }}
                  </vs-td>
                  <vs-td :data="data[indextr].expirationDate">
                    {{ data[indextr].expirationDate | fulldate }}
                  </vs-td>
                  <vs-td :data="data[indextr].resumeDate">
                    {{ data[indextr].resumeDate | fulldate }}
                  </vs-td>
                  <vs-td>
                    <div :style="{ direction: $vs.rtl ? 'rtl' : 'ltr' }">
                      <vx-tooltip text="Edit"
                        ><feather-icon
                          icon="Edit3Icon"
                          svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer"
                          @click="editRecord(data[indextr].id)"
                      /></vx-tooltip>
                      <!-- TODO: Add delete after record-deletion rules and permissions are finalized. -->
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </template>
          <div
            v-if="!isLoadingRecords && !loadError && !sortedData.length"
            class="hours-rendered-record-empty"
          >
            <feather-icon
              :icon="recordView === 'active' ? 'CheckCircleIcon' : 'ClockIcon'"
              svgClasses="h-8 w-8"
            />
            <h4>{{ recordView === "active" ? "No active earnings" : "No earnings history" }}</h4>
            <p>
              {{
                recordView === "active"
                  ? "Add an earning for this employee to get started."
                  : "Inactive earnings will appear here."
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="vx-row">
      <div class="vx-col w-full">
        <vs-button
          v-if="recordView === 'active'"
          type="gradient"
          color="primary"
          :disabled="isLoadingRecords || loadError"
          @click="promptAddNewEarnings()"
          icon-pack="feather"
          icon="icon-plus"
          >Add</vs-button
        >
      </div>
    </div>

    <!-- ADD EARNINGS -->
    <vs-prompt
      class="email-compose"
      title="Add Earnings"
      accept-text="Submit"
      @accept="onAddRecord"
      :is-valid="validForm"
      :active.sync="activePromptAddRecord"
    >
      <vx-card title="Please fill-out the following information">
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Earnings Type</span>
            <span style="color: red">*</span>
            <v-select
              name="earnings type"
              v-validate="'required'"
              class="w-full"
              v-model="earningsType"
              label="earningsTypeName"
              :options="getAllEarningsTypeLookup"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('earnings type')"
              >{{ errors.first("earnings type") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Amount</span><span style="color: red">*</span>
            <vs-input
              name="amount"
              v-validate="'max_value:1000000'"
              class="w-full"
              v-model="earningsAmount"
            />
            <span class="text-danger text-sm" v-show="errors.has('amount')">{{
              errors.first("amount")
            }}</span>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Period</span><span style="color: red">*</span>
            <v-select
              name="period"
              v-validate="'required'"
              class="w-full"
              v-model="period"
              label="periodName"
              :options="getAllPeriodByPaymentMode"
              placeholder="Please Select"
            />
            <span class="text-danger text-sm" v-show="errors.has('period')">{{
              errors.first("period")
            }}</span>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Effectivity Date</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="effectivityDate"
            />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Expiry Date</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="expirationDate"
            />
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Date Resume :</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="resumeDate"
            />
          </div>
          <div class="vx-col sm:w-1/2 w-full md:mt-8">
            <div class="flex">
              <vs-checkbox v-model="isOneTime">One Time?</vs-checkbox>
            </div>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isHold">Hold?</vs-checkbox>
            </div>
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isActive">Active?</vs-checkbox>
            </div>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isIncludedIn13thMonth"
                >Included In 13th Month?</vs-checkbox
              >
            </div>
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isIncludedInFinalPay"
                >Included In Final Pay?</vs-checkbox
              >
            </div>
          </div>
        </div>
      </vx-card>
    </vs-prompt>
    <!-- END ADD EARNINGS -->
    <!-- EDIT EARNINGS -->
    <vs-prompt
      class="email-compose"
      title="Edit Earnings"
      accept-text="Submit"
      @accept="onUpdateRecord"
      :is-valid="validForm"
      :active.sync="activePromptEditRecord"
    >
      <vx-card title="You may update the following information">
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Earnings Type</span>
            <span style="color: red">*</span>
            <v-select
              name="earnings type"
              v-validate="'required'"
              class="w-full"
              v-model="earningsType"
              label="earningsTypeName"
              :options="getAllEarningsTypeLookup"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('earnings type')"
              >{{ errors.first("earnings type") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Amount</span><span style="color: red">*</span>
            <vs-input
              name="amount"
              v-validate="'max_value:1000000'"
              class="w-full"
              v-model="earningsAmount"
            />
            <span class="text-danger text-sm" v-show="errors.has('amount')">{{
              errors.first("amount")
            }}</span>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Period</span><span style="color: red">*</span>
            <v-select
              name="period"
              v-validate="'required'"
              class="w-full"
              v-model="period"
              label="periodName"
              :options="getAllPeriodByPaymentMode"
              placeholder="Please Select"
            />
            <span class="text-danger text-sm" v-show="errors.has('period')">{{
              errors.first("period")
            }}</span>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Effectivity Date</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="effectivityDate"
            />
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Expiry Date</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="expirationDate"
            />
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <span class="text-sm">Date Resume :</span>
            <flat-pickr
              :config="configdateTimePicker"
              class="w-full"
              placeholder="Select Date"
              v-model="resumeDate"
            />
          </div>
          <div class="vx-col sm:w-1/2 w-full md:mt-8">
            <div class="flex">
              <vs-checkbox v-model="isOneTime">One Time?</vs-checkbox>
            </div>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isHold">Hold?</vs-checkbox>
            </div>
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isActive">Active?</vs-checkbox>
            </div>
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isIncludedIn13thMonth"
                >Included In 13th Month?</vs-checkbox
              >
            </div>
          </div>
          <div class="vx-col sm:w-1/2 w-full mb-4">
            <div class="flex">
              <vs-checkbox v-model="isIncludedInFinalPay"
                >Included In Final Pay?</vs-checkbox
              >
            </div>
          </div>
        </div>
      </vx-card>
    </vs-prompt>
    <!-- END EDIT EARNINGS -->
  </div>
</template>
<script>
import moment from "moment";
import vSelect from "vue-select";

import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
export default {
  props: {
    paymentMode: {
      type: Number,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selected: [],

      earningsId: 0,
      earningsType: [],
      earningsAmount: 0,
      period: [],
      isHold: false,
      isOneTime: false,
      isActive: true,
      isIncludedIn13thMonth: false,
      isIncludedInFinalPay: false,
      effectivityDate: null,
      expirationDate: null,
      resumeDate: null,
      dateDeactivated: null,
      dateReActivated: null,
      activePromptAddRecord: false,
      activePromptEditRecord: false,
      isLoadingRecords: false,
      loadError: false,
      recordView: "active",

      configdateTimePicker: {
        allowInput: true,
      },

      currentDate: moment().utc().format("YYYY-MM-DD"),

      sortKey: "",
      sortOrder: 1, // 1 for ascending, -1 for descending
    };
  },
  components: {
    "v-select": vSelect,
    flatPickr,
  },
  computed: {
    getAllEarnings() {
      return this.$store.getters["payroll/getEarningsByUserId"](this.userId);
    },
    getAllEarningsTypeLookup() {
      return this.$store.state.lookup.getAllEarningsType;
    },
    getAllPeriodByPaymentMode() {
      return this.$store.getters["lookup/periodLookupByPaymentMode"](
        this.paymentMode
      );
    },
    earningsTypeName() {
      return (id) => this.$store.getters["lookup/earningsTypeLookup"](id);
    },
    periodName() {
      return (id) => this.$store.getters["lookup/periodLookup"](id);
    },
    validForm() {
      return Boolean(
        this.earningsType &&
          this.earningsType.earningsTypeId &&
          this.earningsAmount !== "" &&
          this.earningsAmount !== null &&
          Number(this.earningsAmount) > 0 &&
          this.period &&
          this.period.periodId
      );
    },
    getDateDiff() {
      return (createdOn) => {
        var oneDay = 1;
        if (createdOn !== null && this.currentDate !== null) {
          var date1 = new Date(moment(createdOn).format("MM-DD-YYYY"));
          var date2 = new Date(moment(this.currentDate).format("MM-DD-YYYY"));

          var timeDiff = Math.abs(date2.getTime() - date1.getTime());
          var diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
          if (diffDays !== "NaN") {
            return diffDays + oneDay;
          } else {
            return 0;
          }
        }
      };
    },
    activeEarnings() {
      return this.getAllEarnings.filter((earning) => Boolean(earning.isActive));
    },
    earningsHistory() {
      return this.getAllEarnings.filter((earning) => !earning.isActive);
    },
    displayedEarnings() {
      return this.recordView === "history"
        ? this.earningsHistory
        : this.activeEarnings;
    },
    sortedData() {
      if (this.sortKey) {
        const sortedData = this.displayedEarnings.slice().sort((a, b) => {
          let aValue = a[this.sortKey];
          let bValue = b[this.sortKey];

          if (aValue == null) return this.sortOrder;
          if (bValue == null) return -this.sortOrder;

          if (typeof aValue === "number" && typeof bValue === "number") {
            return this.sortOrder * (aValue - bValue);
          }
          return this.sortOrder * aValue.localeCompare(bValue);
        });

        return sortedData;
      } else {
        return this.displayedEarnings;
      }
    },
  },
  methods: {
    loadEarningsData() {
      return this.$store.dispatch("payroll/preloadHoursRenderedData", {
        force: true,
      });
    },
    earningsTypeLabel(id) {
      const earningsType = this.earningsTypeName(id);
      return earningsType ? earningsType.earningsTypeName : "Loading…";
    },
    periodLabel(id) {
      const period = this.periodName(id);
      return period ? period.periodName : "Loading…";
    },
    promptAddNewEarnings() {
      this.resetEarningsForm();
      this.activePromptAddRecord = true;
    },
    resetEarningsForm() {
      this.earningsId = 0;
      this.earningsType = [];
      this.earningsAmount = 0;
      this.period = [];
      this.isHold = false;
      this.isOneTime = false;
      this.isActive = true;
      this.isIncludedIn13thMonth = false;
      this.isIncludedInFinalPay = false;
      this.effectivityDate = null;
      this.expirationDate = null;
      this.resumeDate = null;
      this.dateDeactivated = null;
      this.dateReActivated = null;
      this.$nextTick(() => this.$validator.reset());
    },
    editRecord(id) {
      const e = this.$store.getters["payroll/getEarningsById"](id);
      (this.earningsType = this.earningsTypeName(e.earningsTypeId)),
        (this.earningsAmount = e.earningsAmount),
        (this.isOneTime = e.isOneTime),
        (this.period = this.periodName(e.periodId)),
        (this.isHold = e.isHold),
        (this.resumeDate = e.isHold ? e.resumeDate : null),
        (this.effectivityDate = e.effectivityDate),
        (this.expirationDate = e.expirationDate),
        (this.isActive = e.isActive),
        (this.isIncludedIn13thMonth = e.isIncludedIn13thMonth),
        (this.isIncludedInFinalPay = e.isIncludedInFinalPay),
        (this.dateDeactivated = e.dateDeactivated),
        (this.dateReActivated = e.dateReActivated);
      this.earningsId = e.id;
      this.activePromptEditRecord = true;
    },
    onAddRecord() {
      this.$vs.loading();
      const e = {
        earningsTypeId: this.earningsType.earningsTypeId,
        earningsAmount: this.earningsAmount,
        isOneTime: this.isOneTime,
        periodId: this.period.periodId,
        isHold: this.isHold,
        resumeDate: this.isHold ? this.resumeDate : null,
        effectivityDate: this.effectivityDate,
        expirationDate: this.expirationDate,
        isActive: this.isActive,
        isIncludedIn13thMonth: this.isIncludedIn13thMonth,
        isIncludedInFinalPay: this.isIncludedInFinalPay,
        dateDeactivated: this.dateDeactivated,
        dateReActivated: this.dateReActivated,
        userId: this.userId,
        currentUserId: this.$store.state.AppActiveUser.uid,
      };
      this.$store
        .dispatch("payroll/AddEmployeeEarnings", e)
        .then(() => {
          this.$vs.loading.close();
        })
        .catch((error) => {
          this.handleSaveError(error);
        });
    },
    onUpdateRecord() {
      this.$vs.loading();
      const e = {
        earningsTypeId: this.earningsType.earningsTypeId,
        earningsAmount: this.earningsAmount,
        isOneTime: this.isOneTime,
        periodId: this.period.periodId,
        isHold: this.isHold,
        resumeDate: this.isHold ? this.resumeDate : null,
        effectivityDate: this.effectivityDate,
        expirationDate: this.expirationDate,
        isActive: this.isActive,
        isIncludedIn13thMonth: this.isIncludedIn13thMonth,
        isIncludedInFinalPay: this.isIncludedInFinalPay,
        dateDeactivated: this.dateDeactivated,
        dateReActivated: this.dateReActivated,
        userId: this.userId,
        id: this.earningsId,
        currentUserId: this.$store.state.AppActiveUser.uid,
      };
      this.$store
        .dispatch("payroll/UpdateEmployeeEarnings", e)
        .then(() => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Success",
            text: "Earnings updated",
            iconPack: "feather",
            icon: "icon-check",
            color: "success",
          });
        })
        .catch((error) => {
          this.handleSaveError(error);
        });
    },
    handleSaveError(error) {
      this.$vs.loading.close();
      this.$vs.notify({
        title: "Unable to save",
        text: error && error.message ? error.message : "Please try again.",
        iconPack: "feather",
        icon: "icon-x-octagon",
        color: "danger",
      });
    },
    sort(key) {
      if (this.sortKey === key) {
        this.sortOrder *= -1;
      } else {
        this.sortKey = key;
        this.sortOrder = 1;
      }
    },
  },
};
</script>
